











import { Component, Vue, Prop } from 'vue-property-decorator';
import notificationService from '@/services/notificationService';

import { DxForm } from 'devextreme-vue/form';
import Popup from '@/components/default/Popup.vue';

import {
  ICreateCompanyCommand,
  ICreateSubcontractorCommand,
  IUpdateCompanyCommand,
  IUpdateSubcontractorCommand,
  CreateCompanyCommand,
  UpdateCompanyCommand,
  CreateSubcontractorCommand,
  UpdateSubcontractorCommand,
  IAddress,
  Address,
  AddressType,
  ContactsClient,
  ITunnelGroup,
  TunnelGroupsClient,
} from '@/data/web-api-client';

@Component({
  components: {
    Popup,
    DxForm,
  },
})
export default class AddOrUpdateCompanyDialog extends Vue {
  @Prop({ required: true, default: false })
  private isSubcontractor!: boolean;

  @Prop({ required: false })
  private projectId?: string;

  @Prop({ required: false, default: '' })
  private contactId!: string;

  private show: boolean = false;
  private saving: boolean = false;
  private loading: boolean = false;
  private tunnelGroups: ITunnelGroup[] = [];

  private client: ContactsClient = new ContactsClient(undefined, this.$axios);

  private formdata: ICreateCompanyCommand | IUpdateCompanyCommand | ICreateSubcontractorCommand | IUpdateSubcontractorCommand | any = {
    contantPaymentTerms: null,
    email: '',
    phone: '',
    tags: [],
    id: '',
    disabled: false,
    notes: '',
    companyName: '',
    vatNumber: '',
    language: '',
    termsOfPayment: null,
    isSupplier: false,
    addresses: [
      new Address({
        name: '',
        type: AddressType.Default,
        addressline1: '',
        addressline2: '',
        zip: '',
        city: '',
        country: '',
      }),
    ],
    specialty: '',
    projectId: '',
    tunnelGroupsId: '',
  };

  public async mounted() {
    this.tunnelGroups = await new TunnelGroupsClient(undefined, this.$axios).get();
  }

  public async open() {
    this.formdata.projectId = this.projectId;

    this.show = true;

    setTimeout(async () => {
      try {
        if (!this.contactId || this.contactId === '') {
          return;
        }

        this.loading = true;
        const result = await this.client.getContactDetailed(this.contactId);
        Object.assign(this.formdata, result);
      } catch (err) {
        notificationService.showAxiosError(err);
      } finally {
        this.loading = false;
      }
    }, 150);
  }

  private resetForm() {
    Object.assign(this.formdata, {
      contantPaymentTerms: null,
      email: '',
      phone: '',
      tags: [],
      id: '',
      disabled: false,
      notes: '',
      companyName: '',
      vatNumber: '',
      language: '',
      termsOfPayment: null,
      isSupplier: true,
      addresses: [
        new Address({
          name: '',
          type: AddressType.Default,
          addressline1: '',
          addressline2: '',
          zip: '',
          city: '',
          country: '',
        }),
      ],
      specialty: '',
      projectId: '',
      billingDetails: {
        discountPercentage: 0,
      },
    });
  }

  private async save() {
    try {
      const form: any = this.$refs.contactForm;
      const validationResult = form.instance.validate();

      if (validationResult.isValid) {
        // save
        this.saving = true;
        if (!this.contactId || this.contactId === '') {
          if (this.isSubcontractor) {
            const res = await this.client.createSubcontractor(new CreateSubcontractorCommand(this.formdata));
            this.$emit('created', res);
          } else {
            const res = await this.client.createCompany(new CreateCompanyCommand(this.formdata));
            this.$emit('created', res);
          }
        } else {
          const command = this.isSubcontractor ? new UpdateSubcontractorCommand(this.formdata) : new UpdateCompanyCommand(this.formdata);
          command.init(this.formdata);
          const res = this.isSubcontractor ? await this.client.updateSubcontractor(command) : await this.client.updateCompany(command);
          this.$emit('updated');
        }
        notificationService.saved();
        this.show = false;
        this.resetForm();
      } else {
        notificationService.invalidForm();
      }
    } catch (err) {
      notificationService.showAxiosError(err);
    } finally {
      this.saving = false;
    }
  }

  get languageOptions() {
    const list = this.$store.getters['catalogs/list']('Languages');
    let items = [];
    if (list) {
      items = list.values;
    }

    return {
      items,
    };
  }

  get tagsOptions() {
    const list = this.$store.getters['catalogs/list']('CustomerTags');
    let items = [];
    if (list) {
      items = list.values;
    }

    return {
      items,
    };
  }

  get specialtyOptions() {
    const list = this.$store.getters['catalogs/list']('WorkTypes');
    let items = [];
    if (list) {
      items = list.values;
    }

    return {
      items,
    };
  }

  get addressTypeOptions() {
    const options = [
      { text: 'Standaard', value: AddressType.Default },
      { text: 'Leveradres', value: AddressType.DeliveryAddress },
      { text: 'Factuuradres', value: AddressType.InvoicingAddress },
      { text: 'Bezoekadres', value: AddressType.VisitAddress },
    ];
    return {
      dataSource: options,
      valueExpr: 'value',
      displayExpr: 'text',
      searchEnabled: false,
      showClearButton: false,
      showCancelButton: false,
    };
  }

  get tunnelGroupsOptions() {
    const items = this.tunnelGroups.map((dg) => ({
      value: dg.id,
      text: dg.name,
    }));

    return {
      dataSource: items,
      displayExpr: 'text',
      valueExpr: 'value',
      filterExpr: 'value',
    };
  }

  get addressGroup() {
    const items: any[] = [
      {
        itemType: 'button',
        colSpan: 2,
        buttonOptions: {
          icon: 'fas fa-plus',
          text: 'Adres toevoegen',
          onClick: () => {
            this.formdata.addresses.push(
              new Address({
                name: '',
                type: AddressType.Default,
                addressline1: '',
                addressline2: '',
                zip: '',
                city: '',
                country: '',
              })
            );
          },
        },
      },
    ];

    for (let index = 0; index < this.formdata.addresses.length; index++) {
      items.push({
        itemType: 'group',
        items: [
          {
            itemType: 'simple',
            editorOptions: this.addressTypeOptions,
            editorType: 'dxLookup',
            dataField: `addresses[${index}].type`,
            label: {
              text: 'Soort',
            },
          },
          {
            itemType: 'simple',
            dataField: `addresses[${index}].addressline1`,
            label: {
              text: 'Straat + nr/bus',
            },
          },
          {
            itemType: 'simple',
            dataField: `addresses[${index}].zip`,
            label: {
              text: 'Postcode',
            },
          },
          {
            itemType: 'simple',
            dataField: `addresses[${index}].city`,
            label: {
              text: 'Gemeente',
            },
          },
          {
            itemType: 'simple',
            dataField: `addresses[${index}].country`,
            label: {
              text: 'Land',
            },
          },
          {
            itemType: 'empty',
          },
        ],
      });
    }

    return items;
  }

  get formItems() {
    return [
      {
        itemType: 'group',
        colCount: 2,
        items: [
          {
            itemType: 'simple',
            label: {
              text: 'Bedrijfsnaam',
            },
            isRequired: true,
            dataField: 'companyName',
            validationRules: [{ type: 'required', message: 'Veld is verplicht' }],
          },
          {
            itemType: 'simple',
            label: {
              text: 'BTW nummer',
            },
            isRequired: false,
            dataField: 'vatNumber',
          },
          {
            itemType: 'group',
            items: [
              {
                itemType: 'simple',
                label: {
                  text: 'Email',
                },
                isRequired: false,
                dataField: 'email',
                validationRules: [{ type: 'email', message: 'Email is niet correct' }],
              },
              {
                itemType: 'simple',
                label: {
                  text: 'Tel',
                },
                isRequired: false,
                dataField: 'phone',
              },
              {
                itemType: 'simple',
                label: {
                  text: 'Taal',
                },
                isRequired: false,
                dataField: 'language',
                editorOptions: this.languageOptions,
                editorType: 'dxLookup',
              },
            ],
          },
          {
            itemType: 'group',
            items: [
              {
                itemType: 'simple',
                label: {
                  text: 'Tags',
                },
                isRequired: false,
                dataField: 'tags',
                editorOptions: this.tagsOptions,
                editorType: 'dxTagBox',
              },
              {
                itemType: 'simple',
                label: {
                  text: 'Leverancier',
                },
                editorType: 'dxCheckBox',
                isRequired: false,
                dataField: 'isSupplier',
              },
              {
                itemType: 'simple',
                label: {
                  text: 'Betalingstermijn contant',
                },
                editorType: 'dxNumberBox',
                isRequired: false,
                dataField: 'contantPaymentTerms',
              },
              {
                itemType: 'simple',
                label: {
                  text: 'Betalingstermijn',
                },
                editorType: 'dxNumberBox',
                isRequired: false,
                dataField: 'termsOfPayment',
              },
              {
                itemType: 'simple',
                label: {
                  text: 'Specialiteit',
                },
                visible: this.isSubcontractor,
                isRequired: false,
                dataField: 'tags',
                editorOptions: this.specialtyOptions,
                editorType: 'dxSelectBox',
              },
              {
                itemType: 'simple',
                label: {
                  text: this.$t('objects.tunnelGroups.singular'),
                },
                visible: this.isSubcontractor,
                isRequired: false,
                dataField: 'tunnelGroupsId',
                editorOptions: this.tunnelGroupsOptions,
                editorType: 'dxSelectBox',
              },
            ],
          },
        ],
      },
      {
        itemType: 'tabbed',
        cssClass: 'contactTabBg',
        tabPanelOptions: {
          deferRendering: false,
        },
        tabs: [
          // ADDRESS
          {
            title: 'Adres',
            colCount: 2,
            items: this.addressGroup,
          },
          // Notes
          {
            title: 'Notities',
            items: [
              {
                itemType: 'simple',
                dataField: 'notes',
                editorType: 'dxTextArea',
                editorOptions: { height: 100 },
                label: {
                  text: 'notities',
                  visible: false,
                },
              },
            ],
          },
          // Invoicing
          {
            title: 'Kortingen',
            items: [
              {
                itemType: 'simple',
                dataField: 'billingDetails.discountPercentage',
                editorType: 'dxNumberBox',
                label: {
                  text: 'Korting (%)',
                },
              },
            ],
          },
        ],
      },
    ];
  }
}
